import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Timer, Trophy, Users, Loader2 } from "lucide-react";
import { StudentShell } from "../components/StudentShell";
import { useLang } from "../i18n";
import { api } from "../api";

export default function Competition() {
  const { t } = useLang();
  const [comps, setComps] = useState([]);
  const [round, setRound] = useState(null);
  const [i, setI] = useState(0);
  const [left, setLeft] = useState(0);
  const [result, setResult] = useState(null);
  const [board, setBoard] = useState(null);
  const [busy, setBusy] = useState(false);
  const answers = useRef([]);
  const started = useRef(0);
  const sending = useRef(false);

  const load = () => api.get("/competitions").then((r) => setComps(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!board?.id) return;
    const id = setInterval(() => {
      api.get(`/competitions/${board.id}/leaderboard`).then((r) => setBoard((b) => ({ ...b, ...r.data })));
    }, 5000);
    return () => clearInterval(id);
  }, [board?.id]);

  const finish = async (final) => {
    if (sending.current) return;
    sending.current = true;
    const seconds = Math.round((Date.now() - started.current) / 1000);
    try {
      const { data } = await api.post(`/competitions/${round.id}/submit`, { answers: final, seconds });
      setResult({ ...data, seconds });
      const lb = await api.get(`/competitions/${round.id}/leaderboard`);
      setBoard({ id: round.id, title: round.title, ...lb.data });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "failed"); }
  };

  const pick = (choice) => {
    if (!round || result || sending.current) return;
    answers.current = [...answers.current, choice];
    if (answers.current.length >= round.questions.length) finish(answers.current);
    else setI(answers.current.length);
  };

  // countdown per question
  useEffect(() => {
    if (!round || result) return;
    setLeft(round.seconds_per_question);
    const id = setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [round, i, result]);

  // timeout -> auto advance
  useEffect(() => {
    if (round && !result && left === 0 && answers.current.length === i) pick(-1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [left]);

  const join = async (c) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/competitions/${c.id}/round`);
      if (!data.questions?.length) throw new Error("empty");
      answers.current = []; sending.current = false;
      setResult(null); setBoard(null); setI(0); setRound(data);
      started.current = Date.now();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذر بدء المسابقة"); }
    finally { setBusy(false); }
  };

  const openBoard = async (c) => {
    const { data } = await api.get(`/competitions/${c.id}/leaderboard`);
    setBoard({ id: c.id, title: c.title, ...data });
    setRound(null); setResult(null);
  };

  // ---- playing ----
  if (round && !result) {
    const q = round.questions[i];
    const pct = (left / round.seconds_per_question) * 100;
    return (
      <StudentShell>
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black">{round.title}</h2>
            <span className="flex items-center gap-2 font-black" style={{ color: "var(--play-accent)" }}
              data-testid="comp-timer"><Timer size={18} />{left}s</span>
          </div>
          <div className="h-3 rounded-full bg-white/12 mt-3 overflow-hidden">
            <div className="h-full transition-all duration-1000 ease-linear"
              style={{ width: `${pct}%`, background: pct > 40 ? "var(--play-secondary)" : "var(--play-accent)" }} />
          </div>
          <motion.div key={i} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
            className="play-card mt-6" data-testid="comp-question">
            <div className="text-xs font-bold text-white/45">{i + 1} / {round.questions.length}</div>
            <h3 className="text-2xl font-black mt-2" dir="ltr">{q.question}</h3>
            <div className="space-y-3 mt-6">
              {q.options.map((o, k) => (
                <button key={k} onClick={() => pick(k)} data-testid={`comp-option-${k}`} dir="ltr"
                  className="w-full text-start rounded-2xl border-2 px-5 py-4 font-bold hover:-translate-y-0.5 transition-transform"
                  style={{ borderColor: "rgba(255,255,255,.2)" }}>{o}</button>
              ))}
            </div>
          </motion.div>
        </div>
      </StudentShell>
    );
  }

  // ---- result + live board ----
  if (result && board) return (
    <StudentShell>
      <div className="play-card max-w-xl mx-auto text-center" data-testid="comp-result"
        style={{ borderColor: "var(--play-primary)" }}>
        <div className="text-6xl">🏁</div>
        <h2 className="text-2xl font-black mt-3">{t("competitionDone")}</h2>
        <div className="text-5xl font-black mt-2" style={{ color: "var(--play-primary)" }}>
          {result.score}/{result.total}</div>
        <p className="text-white/65 mt-1">{result.seconds}s</p>
      </div>
      <Board board={board} t={t} onBack={() => { setResult(null); setBoard(null); setRound(null); }} />
    </StudentShell>
  );

  if (board) return (
    <StudentShell>
      <Board board={board} t={t} onBack={() => setBoard(null)} />
    </StudentShell>
  );

  // ---- list ----
  return (
    <StudentShell>
      <h1 className="text-4xl font-black text-white glow-text flex items-center gap-3"><Trophy size={30} />{t("competitions")}</h1>
      <div className="grid md:grid-cols-2 gap-6 mt-8">
        {comps.map((c) => (
          <div key={c.id} className="play-card" data-testid={`comp-card-${c.id}`}
            style={{ borderColor: c.is_live ? "var(--play-accent)" : "rgba(255,255,255,.2)" }}>
            <div className="flex justify-between items-start">
              <h3 className="text-xl font-extrabold">{c.title}</h3>
              {c.is_live && <span className="rounded-full px-3 py-1 text-xs font-extrabold text-white"
                style={{ background: "var(--play-accent)" }}>{t("live")}</span>}
            </div>
            <p className="text-sm text-white/65 mt-2">
              {t("grade")} {c.grade} · {c.topic} · {c.question_count} {t("questions")} · {c.seconds_per_question}s</p>
            <p className="text-xs text-white/45 mt-1">
              {c.starts_at ? new Date(c.starts_at).toLocaleString() : "-"} → {c.ends_at ? new Date(c.ends_at).toLocaleString() : "-"}</p>
            {c.not_started && <Countdown to={c.starts_at} t={t} onDone={load} />}
            {c.ended && <div className="mt-3 rounded-2xl px-4 py-2 text-center font-bold text-sm"
              style={{ background: "rgba(120,140,180,.3)", color: "#e8eefc" }}
              data-testid={`comp-ended-${c.id}`}>انتهت</div>}
            <div className="flex gap-2 mt-5">
              {c.my_score === null || c.my_score === undefined ? (
                <button disabled={!c.is_live || busy} onClick={() => join(c)} data-testid={`join-comp-${c.id}`}
                  className="play-btn flex-1 disabled:opacity-100 flex items-center justify-center gap-2"
                  style={c.is_live
                    ? { background: "var(--play-accent)", color: "#fff" }
                    : { background: "rgba(120,140,180,.35)", color: "#e8eefc", boxShadow: "none" }}>
                  {busy ? <Loader2 className="animate-spin" size={16} /> : null}{t("joinCompetition")}</button>
              ) : (
                <div className="flex-1 rounded-full text-center py-3 font-bold text-sm"
                  style={{ background: "rgba(62,196,109,.22)" }} data-testid={`comp-mine-${c.id}`}>
                  {t("alreadyPlayed")} · {c.my_score} ⭐ ({c.my_seconds}s)</div>
              )}
              <button onClick={() => openBoard(c)} data-testid={`board-comp-${c.id}`}
                className="play-btn text-white" style={{ background: "var(--play-secondary)" }}>
                <Trophy size={16} /></button>
            </div>
          </div>
        ))}
        {!comps.length && <div className="play-card text-white/45">{t("noData")}</div>}
      </div>
    </StudentShell>
  );
}

const Countdown = ({ to, t, onDone }) => {
  const [left, setLeft] = useState(Math.max(0, Math.floor((new Date(to) - Date.now()) / 1000)));
  useEffect(() => {
    const id = setInterval(() => {
      const v = Math.max(0, Math.floor((new Date(to) - Date.now()) / 1000));
      setLeft(v);
      if (v === 0) { clearInterval(id); onDone?.(); }
    }, 1000);
    return () => clearInterval(id);
  }, [to, onDone]);
  const d = Math.floor(left / 86400), h = Math.floor((left % 86400) / 3600);
  const m = Math.floor((left % 3600) / 60), s = left % 60;
  return (
    <div className="mt-3 rounded-2xl px-4 py-3 font-black text-center" data-testid="comp-countdown"
      style={{ background: "rgba(255,159,28,.14)", color: "#B45309" }}>
      {t("startsIn")}: {d > 0 ? `${d}د ` : ""}{String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </div>
  );
};

const Board = ({ board, t, onBack }) => (
  <div className="grid md:grid-cols-2 gap-6 mt-8 max-w-4xl mx-auto">
    <div className="md:col-span-2 flex items-center justify-between">
      <h2 className="text-2xl font-black">{board.title}</h2>
      <span className="text-sm font-bold text-white/65 flex items-center gap-1">
        <Users size={15} />{board.participants} {t("participants")}</span>
    </div>
    <div className="play-card" style={{ borderColor: "var(--play-secondary)" }} data-testid="comp-lb-students">
      <h3 className="font-black mb-3">{t("students")}</h3>
      {board.students?.map((s, i) => (
        <div key={i} className="flex items-center gap-3 py-2 border-b border-white/10"
          data-testid={`comp-lb-student-${i}`}>
          <span className="w-7 font-black">{["🥇", "🥈", "🥉"][i] || i + 1}</span>
          <div className="flex-1"><div className="font-bold">{s.name}</div>
            <div className="text-xs text-white/45">{s.school}</div></div>
          <span className="font-black" style={{ color: "var(--play-primary)" }}>
            {s.score}/{s.total} · {s.seconds}s</span>
        </div>
      ))}
      {!board.students?.length && <p className="text-white/45">{t("noData")}</p>}
    </div>
    <div className="play-card" style={{ borderColor: "#38B000" }} data-testid="comp-lb-schools">
      <h3 className="font-black mb-3">{t("schools")}</h3>
      {board.schools?.map((s, i) => (
        <div key={i} className="flex items-center gap-3 py-2 border-b border-white/10"
          data-testid={`comp-lb-school-${i}`}>
          <span className="w-7 font-black">{["🥇", "🥈", "🥉"][i] || i + 1}</span>
          <div className="flex-1 font-bold">{s.school}</div>
          <span className="font-black" style={{ color: "#38B000" }}>{s.score}</span>
        </div>
      ))}
      {!board.schools?.length && <p className="text-white/45">{t("noData")}</p>}
    </div>
    <button onClick={onBack} data-testid="comp-back-btn"
      className="md:col-span-2 pro-btn bg-white/12 mx-auto">{t("back")}</button>
  </div>
);
